import type { Connection, ConnectionCreate } from "src/api/connectApi/types";
import { taktileInternalPrefix } from "src/api/constants";
import { WorkspaceDataplane } from "src/clients/flow-api";

export const generateCaseFlowConnectionName = (workspaceId: string): string => {
  return `${taktileInternalPrefix}CF_${workspaceId}`;
};

const ensureProtocol = (url: string | null | undefined): string => {
  if (!url || url.trim() === "") {
    throw new Error("Base URL cannot be empty or undefined");
  }

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `https://${url}`;
};

export const generateCaseFlowConnectionPayload = (
  workspace: WorkspaceDataplane,
): ConnectionCreate => {
  const baseUrlWithProtocol = ensureProtocol(workspace.base_url);

  return {
    name: generateCaseFlowConnectionName(workspace.id),
    is_sandbox: false,
    configuration: {
      base_url: `${baseUrlWithProtocol}/case_data`,
      probe_url: "",
      allow_test_invocations: true,
      auth_settings: {
        auth_method: "no_auth",
        ssl_enabled: false,
      },
    },
    secrets: [],
    provider: "custom",
    enable_non_prod_configs: false,
    non_prod_env_configs: {},
    ...(workspace.settings?.data_retention && {
      data_retention: workspace.settings.data_retention,
    }),
    has_raw_requests_enabled_in_resource: true,
  };
};

export const findCaseFlowConnection = (
  connections: Connection[],
  workspace: WorkspaceDataplane,
): Connection | undefined => {
  const expectedName = generateCaseFlowConnectionName(workspace.id);
  return connections.find(
    (connection) =>
      connection.name === expectedName && connection.provider === "custom",
  );
};

const needsConfigurationUpdate = (
  existingConnection: Connection,
  expectedPayload: ConnectionCreate,
): boolean => {
  const existingConfig = existingConnection.configuration;
  const expectedConfig = expectedPayload.configuration;

  const isDataRetentionEqual = (
    existing?: { value: number; unit: string },
    expected?: { value: number; unit: string },
  ): boolean => {
    if (!existing && !expected) return true;
    if (!existing || !expected) return false;
    return existing.value === expected.value && existing.unit === expected.unit;
  };

  return (
    existingConfig.base_url !== expectedConfig.base_url ||
    existingConfig.probe_url !== expectedConfig.probe_url ||
    existingConfig.allow_test_invocations !==
      expectedConfig.allow_test_invocations ||
    !isDataRetentionEqual(
      existingConnection.data_retention,
      expectedPayload.data_retention,
    )
  );
};

export type CaseFlowConnectionActions = {
  create: (payload: ConnectionCreate) => Promise<Connection>;
  update: ({
    id,
    payload,
  }: {
    id: string;
    payload: ConnectionCreate;
  }) => Promise<Connection>;
};

export const ensureCaseFlowConnection = async (
  workspace: WorkspaceDataplane,
  actions: CaseFlowConnectionActions,
  existingConnections: Connection[] = [],
): Promise<Connection | null> => {
  try {
    const connectionPayload = generateCaseFlowConnectionPayload(workspace);

    const existingConnection = findCaseFlowConnection(
      existingConnections,
      workspace,
    );

    if (existingConnection) {
      if (needsConfigurationUpdate(existingConnection, connectionPayload)) {
        const updatedConnection = await actions.update({
          id: existingConnection.id,
          payload: connectionPayload,
        });
        return updatedConnection;
      }
      return existingConnection;
    }

    const newConnection = await actions.create(connectionPayload);
    return newConnection;
  } catch {
    return null;
  }
};
